import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import NotFoundRecords from 'src/components/NotFoundRecords';
import SantanderPaymentDetailsRow from './SantanderPaymentDetailsRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ paymentDetails, reportId }) =>
  paymentDetails.map(payment => (
    <SantanderPaymentDetailsRow
      key={payment.id}
      {...payment}
      reportId={reportId}
      allPayments={paymentDetails}
    />
  ));

const SantanderPaymentDetailsTable = ({
  paymentDetails,
  loading,
  success,
  count,
  page,
  handleChangePage,
  reportId,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(() => () => (
    <TableRow component="tr">
      <TableCell variant="head" align="center" key="client">
        {t('SANTANDER_REPORTS.CLIENT')}
      </TableCell>
      <TableCell variant="head" align="center" key="amount">
        {t('SANTANDER_REPORTS.TRANSFER_AMOUNT')}
      </TableCell>
      <TableCell variant="head" align="center" key="clabe">
        {t('SANTANDER_REPORTS.CLABE')}
      </TableCell>
      <TableCell variant="head" align="center" key="paymentMethod">
        {t('SANTANDER_REPORTS.PAYMENT_METHOD')}
      </TableCell>
      <TableCell variant="head" align="center" key="reconciliation">
        {t('SANTANDER_REPORTS.RECONCILIATION_STATUS')}
      </TableCell>
      <TableCell variant="head" align="center" key="reconciledAmount">
        {t('SANTANDER_REPORTS.RECONCILED_AMOUNT')}
      </TableCell>
      <TableCell variant="head" align="center" key="unreconciledAmount">
        {t('SANTANDER_REPORTS.UNRECONCILED_AMOUNT')}
      </TableCell>
      <TableCell sx={{ width: '10%' }} variant="head" key="actions" />
    </TableRow>
  ));

  const tbody = useMemo(() => () => <Tbody paymentDetails={paymentDetails} reportId={reportId} />, [paymentDetails, reportId]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, count, t]
  );

  if (success && !loading && !paymentDetails.length) {
    return (
      <Card>
        <NotFoundRecords title="NOT_FOUND_RECORDS_MESSAGE.SANTANDER_PAYMENT_DETAILS_TITLE" />
      </Card>
    );
  }

  return (
    <Table
      {...rest}
      tbody={tbody}
      theads={theads}
      loading={loading}
      pagination={pagination}
    />
  );
};

SantanderPaymentDetailsTable.propTypes = {
  paymentDetails: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  success: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  handleChangePage: PropTypes.func.isRequired,
  reportId: PropTypes.string.isRequired
};

export default SantanderPaymentDetailsTable;
